import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Typography,
  Box,
  Button,
  Chip,
  Paper,
  CircularProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Snackbar,
  Alert,
  Drawer,
  Badge,
  IconButton,
  LinearProgress,
  Collapse,
  Rating,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as EmptyIcon,
  Lock as LockIcon,
  FiberManualRecord as DotIcon,
  HelpOutline as HelpIcon,
  ArrowBack as BackIcon,
  Timer as TimerIcon,
  Wifi as WifiIcon,
  WifiOff as WifiOffIcon,
  SendOutlined,
  CloseOutlined,
  ChatBubbleOutlineOutlined,
  NoteAltOutlined as NoteAltOutlinedIcon,
  DesktopWindows as DesktopIcon,
  Language as WebAppIcon,
  MenuBook as IntroIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Chat as ChatIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  ContentCopy as ContentCopyIcon,
  DeleteOutline as DeleteOutlineIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { progressService } from '../../services/progress.service';
import { feedbackService } from '../../services/feedback.service';
import { helpRequestService } from '../../services/help-request.service';
import { guacamoleService, DesktopInfo } from '../../services/guacamole.service';
import { StepProgress, SessionProgress, CompletionStats } from '../../types/progress.types';
import { DifficultyPerception } from '../../types/feedback.types';
import { HelpRequest, HelpRequestMessage, MessageReaction as MessageReactionType } from '../../types/help-request.types';
import { useNotification } from '../../context/NotificationContext';
import { useSessionSocket } from '../../hooks/useSessionSocket';
import { useAuth } from '../../hooks/useAuth';
import { socketService, WsSessionChatMessage } from '../../services/socket.service';
import { BlockRenderer } from '../../components/workshop/blocks';
import { sessionService } from '../../services/session.service';
import { StudentCredentials } from '../../types/session.types';
import EnvironmentPanel from '../../components/student/EnvironmentPanel';
import EmojiReactionBar from '../../components/student/EmojiReactionBar';
import MessageReactions from '../../components/chat/MessageReactions';
import { workbenchService } from '../../services/workbench.service';

const HELP_COOLDOWN_MS = 30_000;

const stepTypeIcon: Record<string, React.ReactElement> = {
  remote_desktop: <DesktopIcon sx={{ fontSize: 14 }} />,
  web_app: <WebAppIcon sx={{ fontSize: 14 }} />,
  introduction: <IntroIcon sx={{ fontSize: 14 }} />,
};

const WorkshopView: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [progressData, setProgressData] = useState<SessionProgress | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [actionLoading, setActionLoading] = useState(false);

  // Checklist state
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  // Step list collapse state
  const [stepListExpanded, setStepListExpanded] = useState(false);

  // Help request state
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpMessage, setHelpMessage] = useState('');
  const [helpPriority, setHelpPriority] = useState<'normal' | 'high'>('normal');
  const [helpSending, setHelpSending] = useState(false);
  const [helpCooldown, setHelpCooldown] = useState(false);

  // Drawer mutual exclusivity
  type ActiveDrawer = 'chat' | 'help' | 'workbench' | null;
  const [activeDrawer, setActiveDrawer] = useState<ActiveDrawer>(null);

  // Conversation panel state
  const [activeHelpRequest, setActiveHelpRequest] = useState<HelpRequest | null>(null);
  const [helpMessages, setHelpMessages] = useState<HelpRequestMessage[]>([]);
  const [helpNewMessage, setHelpNewMessage] = useState('');
  const [helpSendingMsg, setHelpSendingMsg] = useState(false);
  const [helpUnreadCount, setHelpUnreadCount] = useState(0);
  const [resolveConfirmOpen, setResolveConfirmOpen] = useState(false);

  // Refs for WebSocket callbacks (avoid stale closures)
  const activeHelpRequestRef = useRef(activeHelpRequest);
  activeHelpRequestRef.current = activeHelpRequest;
  const helpPanelOpenRef = useRef(activeDrawer === 'help');
  helpPanelOpenRef.current = activeDrawer === 'help';
  const helpMessagesEndRef = useRef<HTMLDivElement>(null);

  // Completion state
  const [showCompletion, setShowCompletion] = useState(false);
  const [completionStats, setCompletionStats] = useState<CompletionStats | null>(null);

  // Remote desktop state
  const [desktopInfo, setDesktopInfo] = useState<DesktopInfo | null>(null);
  const [desktopLoading, setDesktopLoading] = useState(false);
  const [desktopError, setDesktopError] = useState<string | null>(null);

  // Grace period desktop status (for completed sessions)
  const [graceStatus, setGraceStatus] = useState<'loading' | 'grace' | 'expired' | 'destroyed' | 'none'>('loading');
  const [graceTtlExpiresAt, setGraceTtlExpiresAt] = useState<string | null>(null);
  const [practiceActive, setPracticeActive] = useState(false);

  // Student environment credentials
  const [studentCredentials, setStudentCredentials] = useState<StudentCredentials | null>(null);

  // WebSocket snackbar state
  const [broadcastMsg, setBroadcastMsg] = useState<string | null>(null);
  const [helpResolvedMsg, setHelpResolvedMsg] = useState<string | null>(null);

  // Session chat state
  const [chatMessages, setChatMessages] = useState<WsSessionChatMessage[]>([]);
  const [chatUnread, setChatUnread] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const chatPanelOpenRef = useRef(false);
  useEffect(() => { chatPanelOpenRef.current = activeDrawer === 'chat'; }, [activeDrawer]);

  // WorkBench state
  const [wbContent, setWbContent] = useState('');
  const [wbSavedContent, setWbSavedContent] = useState('');
  const [wbSaving, setWbSaving] = useState(false);
  const [wbLastSaved, setWbLastSaved] = useState<string | null>(null);
  const [wbLoading, setWbLoading] = useState(false);
  const [wbReadOnly, setWbReadOnly] = useState(false);
  const [wbClearConfirmOpen, setWbClearConfirmOpen] = useState(false);
  const wbSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // WorkBench: load on open
  useEffect(() => {
    if (activeDrawer !== 'workbench') return;
    setWbLoading(true);
    workbenchService.getWorkbench(sessionId!)
      .then((data) => {
        setWbContent(data.content);
        setWbSavedContent(data.content);
        setWbLastSaved(data.updatedAt);
      })
      .catch(() => {})
      .finally(() => setWbLoading(false));
  }, [activeDrawer, sessionId]);

  // WorkBench: cleanup timeout on unmount
  useEffect(() => {
    return () => { if (wbSaveTimeoutRef.current) clearTimeout(wbSaveTimeoutRef.current); };
  }, []);

  const saveWbContent = useCallback(async (value: string) => {
    if (value === wbSavedContent) return;
    setWbSaving(true);
    try {
      const result = await workbenchService.updateWorkbench(sessionId!, value);
      setWbSavedContent(value);
      setWbLastSaved(result.updatedAt);
    } catch (err: any) {
      if (err?.response?.status === 409) setWbReadOnly(true);
    } finally {
      setWbSaving(false);
    }
  }, [sessionId, wbSavedContent]);

  const handleWbChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value.slice(0, 10000);
    setWbContent(value);
    if (wbSaveTimeoutRef.current) clearTimeout(wbSaveTimeoutRef.current);
    wbSaveTimeoutRef.current = setTimeout(() => saveWbContent(value), 1000);
  }, [saveWbContent]);

  const handleCopyAll = useCallback(async () => {
    await navigator.clipboard.writeText(wbContent);
    showNotification('Copied to clipboard', 'success');
  }, [wbContent]);

  // Double-mount guard
  const joinedRef = useRef(false);

  // Guacamole iframe ref — needed to focus the contentWindow for keyboard input
  const guacIframeRef = useRef<HTMLIFrameElement>(null);
  const guacClientRef = useRef<any>(null);

  // Track current step type for clipboard handlers (avoids stale closure)
  const currentStepTypeRef = useRef<string>('introduction');

  // Split panel state (draggable + collapsible)
  const [splitRatio, setSplitRatio] = useState<number>(() => {
    const saved = localStorage.getItem('praxis-split-ratio');
    return saved ? Number(saved) : 35;
  });
  const [isInstructionsCollapsed, setIsInstructionsCollapsed] = useState(() => {
    return localStorage.getItem('praxis-instructions-collapsed') === 'true';
  });
  const [isDragging, setIsDragging] = useState(false);
  const splitContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);

  const focusIframeContent = useCallback(() => {
    try {
      guacIframeRef.current?.contentWindow?.focus();
    } catch (_) { /* cross-origin safety — should not happen with our proxy */ }
  }, []);

  // Get the Guacamole client object from inside the same-origin iframe
  const getGuacClient = useCallback((): any => {
    // Return cached client if already found
    if (guacClientRef.current) return guacClientRef.current;
    try {
      const iframe = guacIframeRef.current;
      if (!iframe?.contentWindow) return null;
      const iframeWin = iframe.contentWindow as any;
      // Try cached client set by injected script
      if (iframeWin.__workshubClient) {
        guacClientRef.current = iframeWin.__workshubClient;
        return guacClientRef.current;
      }
      // Fallback: traverse Angular scope
      if (!iframe.contentDocument) return null;
      const angular = iframeWin.angular;
      if (!angular) return null;
      // Try multiple potential scope elements
      for (const selector of ['.client-main', '.display', '.client-tile', '.tiled-client-grid']) {
        const el = iframe.contentDocument.querySelector(selector);
        if (!el) continue;
        const scope = angular.element(el).scope();
        const client = scope?.client?.client || scope?.managedClient?.client || null;
        if (client && typeof client.sendKeyEvent === 'function') {
          guacClientRef.current = client;
          return client;
        }
      }
      return null;
    } catch { return null; }
  }, []);

  // Send text to the remote session's clipboard via Guacamole client API
  const sendTextToRemoteClipboard = useCallback((text: string) => {
    const iframe = guacIframeRef.current;
    if (!iframe?.contentWindow) return false;
    try {
      const client = getGuacClient();
      if (client) {
        const GuacLib = (iframe.contentWindow as any).Guacamole;
        if (!GuacLib) return false;
        const stream = client.createClipboardStream('text/plain');
        const writer = new GuacLib.StringWriter(stream);
        writer.sendText(text);
        writer.sendEnd();
        return true;
      }
    } catch (err) {
      console.warn('Guacamole clipboard sync failed:', err);
    }
    return false;
  }, [getGuacClient]);

  // Fallback paste: simulate typing each character via Guacamole key events
  const simulateTyping = useCallback((text: string) => {
    const client = getGuacClient();
    if (!client) return false;
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i);
      // Guacamole keysym for printable ASCII = Unicode codepoint
      // For non-ASCII, use 0x01000000 | codepoint
      const keysym = code >= 0x0020 && code <= 0x007E ? code : 0x01000000 | code;
      client.sendKeyEvent(1, keysym); // keydown
      client.sendKeyEvent(0, keysym); // keyup
    }
    return true;
  }, [getGuacClient]);

  // Inject CSS + clipboard interceptor into the Guacamole iframe
  const handleIframeLoad = useCallback(() => {
    focusIframeContent();
    const iframe = guacIframeRef.current;
    if (!iframe) return;

    try {
      const iframeDoc = iframe.contentDocument;
      if (!iframeDoc) return;

      // --- Inject CSS to fill viewport (eliminate black bars) ---
      const style = iframeDoc.createElement('style');
      style.textContent = `
        /* Wrapper elements fill their container */
        .viewport, .client-view, .client-view-content, .client-body,
        .tiled-client-grid, .tiled-client-row, .tiled-client-cell,
        .client-tile, .client-main {
          width: 100% !important;
          height: 100% !important;
        }
        /* Display containers fill available space and center content */
        .displayOuter {
          width: 100% !important;
          height: 100% !important;
          overflow: hidden !important;
          background: #1e1e2e !important;
        }
        .displayMiddle {
          width: 100% !important;
          height: 100% !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        /* Let Guacamole keep its native transform: scale() on .display children */
        .menu { display: none !important; }
      `;
      iframeDoc.head.appendChild(style);

      // --- Inject clipboard support script ---
      // Must run inside the iframe's native JS context so that:
      // 1. navigator.clipboard uses the iframe's origin (same-origin via proxy)
      // 2. Listeners on `window` fire BEFORE Guacamole.Keyboard's `document` listeners
      //    (capture phase order: window → document → ... → target)
      // 3. No cross-frame quirks with event objects or clipboard API
      const script = iframeDoc.createElement('script');
      script.textContent = `
(function() {
  var interval = setInterval(function() {
    try {
      if (!window.angular || !window.Guacamole) return;
      var mainEl = document.querySelector('.client-main');
      if (!mainEl) return;
      var scope = angular.element(mainEl).scope();
      if (!scope) return;
      var client = (scope.client && scope.client.client) || null;
      if (!client) return;
      clearInterval(interval);

      // Expose client for parent frame (Paste button)
      window.__workshubClient = client;
      console.log('[Praxis] Guacamole client hooked');

      // --- Conditional mouse coordinate fix ---
      // Only apply when CSS stretch is active (aspect ratio mismatch).
      // With native Guacamole scaling, aspect ratios match and fix is skipped.
      var canvas = document.querySelector('.display canvas');
      if (canvas) {
        var rect = canvas.getBoundingClientRect();
        var cssAspect = rect.width / rect.height;
        var nativeAspect = canvas.width / canvas.height;
        var isStretched = Math.abs(cssAspect - nativeAspect) > 0.1;
        if (isStretched) {
          var origSendMouse = client.sendMouseState;
          client.sendMouseState = function(state) {
            var c = document.querySelector('.display canvas');
            if (c) {
              var r = c.getBoundingClientRect();
              if (r.width > 0 && r.height > 0) {
                var sx = c.width / r.width;
                var sy = c.height / r.height;
                if (Math.abs(sx - 1) > 0.05 || Math.abs(sy - 1) > 0.05) {
                  state = new Guacamole.Mouse.State(
                    Math.round(state.x * sx),
                    Math.round(state.y * sy),
                    state.left, state.middle, state.right,
                    state.up, state.down
                  );
                }
              }
            }
            origSendMouse.call(client, state);
          };
          console.log('[Praxis] Mouse coordinate fix applied (CSS stretch detected)');
        } else {
          console.log('[Praxis] Native Guacamole scaling — mouse fix skipped');
        }
      }

      function syncClipboard(text) {
        try {
          var stream = client.createClipboardStream('text/plain');
          var writer = new Guacamole.StringWriter(stream);
          writer.sendText(text);
          writer.sendEnd();
          return true;
        } catch(e) {
          console.warn('[Praxis] clipboard stream error:', e);
          return false;
        }
      }

      function sendCtrlV() {
        client.sendKeyEvent(1, 0xFFE3);
        client.sendKeyEvent(1, 0x0076);
        client.sendKeyEvent(0, 0x0076);
        client.sendKeyEvent(0, 0xFFE3);
      }

      // Ctrl+V interceptor — WINDOW-level capture phase fires before
      // Guacamole.Keyboard's DOCUMENT-level capture phase listener.
      var pasteIntercepted = false;
      window.addEventListener('keydown', function(e) {
        if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
          e.stopPropagation();
          e.preventDefault();
          pasteIntercepted = true;

          if (navigator.clipboard && navigator.clipboard.readText) {
            navigator.clipboard.readText().then(function(text) {
              if (text) syncClipboard(text);
              // Send Ctrl+V after clipboard data is on the wire
              setTimeout(sendCtrlV, 100);
            }).catch(function(err) {
              console.warn('[Praxis] clipboard read failed:', err);
              sendCtrlV();
            });
          } else {
            sendCtrlV();
          }
        }
      }, true);

      // Swallow the V keyup so Guacamole.Keyboard state stays clean
      window.addEventListener('keyup', function(e) {
        if (pasteIntercepted && e.key.toLowerCase() === 'v') {
          e.stopPropagation();
          e.preventDefault();
          pasteIntercepted = false;
        }
      }, true);

      // Proactive clipboard sync on every click inside remote desktop.
      // This pre-fills the remote clipboard so even native Ctrl+V works.
      document.addEventListener('mousedown', function() {
        if (navigator.clipboard && navigator.clipboard.readText) {
          navigator.clipboard.readText().then(function(text) {
            if (text) syncClipboard(text);
          }).catch(function() {});
        }
      }, true);

      // --- Remote → Local clipboard ---
      // Listen for clipboard updates from the remote desktop (e.g. user does Ctrl+C in VM).
      // Stores text for the "Copy from Remote" button and tries auto-sync to local clipboard.
      client.onclipboard = function(stream, mimetype) {
        if (mimetype !== 'text/plain') return;
        var reader = new Guacamole.StringReader(stream);
        var data = '';
        reader.ontext = function(text) { data += text; };
        reader.onend = function() {
          window.__workshubRemoteClipboard = data;
          console.log('[Praxis] Remote clipboard updated (' + data.length + ' chars)');
          // Try auto-sync — works when browser grants permission without gesture
          if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(data).catch(function() {});
          }
        };
      };

    } catch(e) { /* Guacamole not ready yet */ }
  }, 500);
})();
      `;
      iframeDoc.head.appendChild(script);

    } catch (err) {
      console.warn('Could not inject Guacamole iframe fixes:', err);
    }
  }, [focusIframeContent]);

  // Paste dialog state
  const [pasteDialogOpen, setPasteDialogOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');

  // Copy-from-remote dialog state
  const [copyDialogOpen, setCopyDialogOpen] = useState(false);
  const [remoteClipboardText, setRemoteClipboardText] = useState('');

  // Try reading clipboard directly; fall back to dialog if clipboard read fails or is empty
  const handlePasteToRemote = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text) {
        if (currentStepTypeRef.current === 'web_app') {
          await navigator.clipboard.writeText(text);
          showNotification('Copied to clipboard — press Ctrl+V in the web app to paste', 'success');
        } else {
          sendTextToRemoteClipboard(text);
          simulateTyping(text);
          showNotification('Text pasted into remote session', 'success');
          setTimeout(focusIframeContent, 100);
        }
        return;
      }
    } catch {
      // Clipboard read failed — fall back to dialog
    }
    setPasteText('');
    setPasteDialogOpen(true);
  }, [showNotification, sendTextToRemoteClipboard, simulateTyping, focusIframeContent]);

  // Send text from the paste dialog to the remote session (or clipboard for web apps)
  const handlePasteDialogSend = useCallback(() => {
    const text = pasteText;
    setPasteDialogOpen(false);
    setPasteText('');
    if (!text) {
      showNotification('No text to paste', 'info');
      return;
    }
    // Web App steps: write to system clipboard so user can Ctrl+V in the iframe
    if (currentStepTypeRef.current === 'web_app') {
      navigator.clipboard.writeText(text).then(() => {
        showNotification('Copied to clipboard — press Ctrl+V in the web app to paste', 'success');
      }).catch(() => {
        showNotification('Clipboard write failed — copy the text manually', 'warning');
      });
      return;
    }
    // RDP steps: use Guacamole client API
    const client = getGuacClient();
    if (!client) {
      showNotification('Remote connection not ready — try again in a moment', 'warning');
      return;
    }
    // Sync clipboard stream (so Ctrl+V also works after this)
    sendTextToRemoteClipboard(text);
    // Simulate typing each character directly via key events
    const typed = simulateTyping(text);
    if (typed) {
      showNotification('Text pasted into remote session', 'success');
    } else {
      showNotification('Could not send key events to remote', 'warning');
    }
    // Refocus the iframe after dialog closes
    setTimeout(focusIframeContent, 100);
  }, [pasteText, getGuacClient, sendTextToRemoteClipboard, simulateTyping, showNotification, focusIframeContent]);

  // Open copy-from-remote dialog — reads remote clipboard from iframe or system clipboard for web apps
  const handleCopyFromRemote = useCallback(() => {
    if (currentStepTypeRef.current === 'web_app') {
      navigator.clipboard.readText().then((text) => {
        setRemoteClipboardText(text || '');
        setCopyDialogOpen(true);
      }).catch(() => {
        setRemoteClipboardText('');
        setCopyDialogOpen(true);
      });
      return;
    }
    const iframe = guacIframeRef.current;
    const text = (iframe?.contentWindow as any)?.__workshubRemoteClipboard || '';
    setRemoteClipboardText(text);
    setCopyDialogOpen(true);
  }, []);

  // Copy remote clipboard text to local clipboard
  const handleCopyDialogCopy = useCallback(() => {
    if (remoteClipboardText) {
      navigator.clipboard.writeText(remoteClipboardText).then(() => {
        showNotification('Copied to your clipboard!', 'success');
        setCopyDialogOpen(false);
      }).catch(() => {
        showNotification('Clipboard write failed — select the text and copy manually', 'warning');
      });
    }
  }, [remoteClipboardText, showNotification]);

  // Drag handle handler for split panel resize
  const handleDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDraggingRef.current = true;
    setIsDragging(true);

    const handleDragMove = (moveEvent: MouseEvent) => {
      if (!isDraggingRef.current || !splitContainerRef.current) return;
      const containerRect = splitContainerRef.current.getBoundingClientRect();
      const newRatio = ((moveEvent.clientX - containerRect.left) / containerRect.width) * 100;
      setSplitRatio(Math.max(20, Math.min(60, newRatio)));
    };

    const handleDragEnd = () => {
      isDraggingRef.current = false;
      setIsDragging(false);
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  // Poll for Guacamole client from parent frame (backup for script injection)
  useEffect(() => {
    if (!desktopInfo?.embedUrl) return;
    const interval = setInterval(() => {
      const client = getGuacClient();
      if (client) {
        clearInterval(interval);
        // Set up fallback hooks from parent if script injection didn't work
        const iframe = guacIframeRef.current;
        if (iframe?.contentWindow && !(iframe.contentWindow as any).__workshubClient) {
          (iframe.contentWindow as any).__workshubClient = client;
          // Apply mouse coordinate fix only if CSS stretch is active (aspect ratio mismatch)
          try {
            const mcCanvas = iframe.contentDocument?.querySelector('.display canvas') as HTMLCanvasElement | null;
            const needsMouseFix = mcCanvas ? (() => {
              const mcRect = mcCanvas.getBoundingClientRect();
              const cssAspect = mcRect.width / mcRect.height;
              const nativeAspect = mcCanvas.width / mcCanvas.height;
              return Math.abs(cssAspect - nativeAspect) > 0.1;
            })() : false;
            if (needsMouseFix) {
              const origSendMouse = client.sendMouseState;
              if (origSendMouse) {
                const GuacMouse = (iframe.contentWindow as any).Guacamole?.Mouse;
                client.sendMouseState = function(state: any) {
                  const canvas = iframe.contentDocument?.querySelector('.display canvas') as HTMLCanvasElement | null;
                  if (canvas) {
                    const rect = canvas.getBoundingClientRect();
                    if (rect.width > 0 && rect.height > 0) {
                      const sx = canvas.width / rect.width;
                      const sy = canvas.height / rect.height;
                      if (Math.abs(sx - 1) > 0.05 || Math.abs(sy - 1) > 0.05) {
                        if (GuacMouse?.State) {
                          state = new GuacMouse.State(
                            Math.round(state.x * sx), Math.round(state.y * sy),
                            state.left, state.middle, state.right, state.up, state.down,
                          );
                        } else {
                          state = { ...state, x: Math.round(state.x * sx), y: Math.round(state.y * sy) };
                        }
                      }
                    }
                  }
                  origSendMouse.call(client, state);
                };
              }
            }
          } catch (err) {
            console.warn('[Praxis] Could not apply mouse fix from parent:', err);
          }
          try {
            const iframeWin = iframe.contentWindow;
            const GuacLib = (iframeWin as any).Guacamole;
            iframeWin.addEventListener('keydown', (e: Event) => {
              const ke = e as KeyboardEvent;
              if ((ke.ctrlKey || ke.metaKey) && ke.key.toLowerCase() === 'v') {
                e.stopPropagation();
                e.preventDefault();
                // Read clipboard from the iframe's navigator (user gesture = keydown)
                const iframeNav = (iframeWin as any).navigator as Navigator;
                const readClip = iframeNav?.clipboard?.readText
                  ? iframeNav.clipboard.readText()
                  : navigator.clipboard.readText(); // fallback to parent navigator
                readClip.then((text: string) => {
                  if (text && GuacLib) {
                    const stream = client.createClipboardStream('text/plain');
                    const writer = new GuacLib.StringWriter(stream);
                    writer.sendText(text);
                    writer.sendEnd();
                  }
                  setTimeout(() => {
                    client.sendKeyEvent(1, 0xFFE3);
                    client.sendKeyEvent(1, 0x0076);
                    client.sendKeyEvent(0, 0x0076);
                    client.sendKeyEvent(0, 0xFFE3);
                  }, 100);
                }).catch(() => {
                  // Clipboard denied — just send Ctrl+V without clipboard sync
                  client.sendKeyEvent(1, 0xFFE3);
                  client.sendKeyEvent(1, 0x0076);
                  client.sendKeyEvent(0, 0x0076);
                  client.sendKeyEvent(0, 0xFFE3);
                });
              }
            }, true);
            iframeWin.addEventListener('keyup', (e: Event) => {
              const ke = e as KeyboardEvent;
              if (ke.key.toLowerCase() === 'v' && (ke.ctrlKey || ke.metaKey)) {
                e.stopPropagation();
                e.preventDefault();
              }
            }, true);
          } catch (err) {
            console.warn('[Praxis] Could not add parent-frame keyboard hooks:', err);
          }
        }
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [desktopInfo?.embedUrl, getGuacClient]);

  // Auto-scroll messages to bottom
  useEffect(() => {
    helpMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [helpMessages]);

  const handleReactionsChange = useCallback((messageId: string, reactions: MessageReactionType[]) => {
    setHelpMessages(prev => prev.map(m =>
      m.id === messageId ? { ...m, reactions } : m,
    ));
  }, []);

  // WebSocket connection
  const { connectionStatus } = useSessionSocket({
    sessionId: sessionId || '',
    onHelpMessage: (data) => {
      const current = activeHelpRequestRef.current;
      if (current && data.helpRequestId === current.id) {
        if (data.userId === user?.id) {
          if (data.status) {
            setActiveHelpRequest(prev => prev ? { ...prev, status: data.status as any } : prev);
          }
          return;
        }
        setHelpMessages(prev => [...prev, {
          id: data.messageId,
          helpRequestId: data.helpRequestId,
          userId: data.userId,
          userRole: data.userRole,
          message: data.message,
          createdAt: data.createdAt,
          user: data.userName ? {
            id: data.userId,
            firstName: data.userName.split(' ')[0],
            lastName: data.userName.split(' ').slice(1).join(' '),
          } : undefined,
        }]);
        if (data.status) {
          setActiveHelpRequest(prev => prev ? { ...prev, status: data.status as any } : prev);
        }
        if (!helpPanelOpenRef.current) {
          setHelpUnreadCount(prev => prev + 1);
        }
      }
    },
    onHelpResolved: (data) => {
      if (data.studentId === user?.id) {
        setActiveHelpRequest(prev => prev ? { ...prev, status: 'resolved' } : null);
        setHelpResolvedMsg(data.response || 'Your help request has been resolved!');
        setHelpCooldown(false);
      }
    },
    onBroadcast: (data) => {
      setBroadcastMsg(data.message);
    },
    onMessageReaction: (data) => {
      const current = activeHelpRequestRef.current;
      if (!current || data.helpRequestId !== current.id) return;
      // Refetch messages to get updated reactions
      helpRequestService.getMessages(current.id).then(setHelpMessages).catch(() => {});
    },
    onSessionChatMessage: (data) => {
      setChatMessages(prev => [...prev, data]);
      if (!chatPanelOpenRef.current) {
        setChatUnread(prev => prev + 1);
      }
    },
    onSessionEnded: () => {
      // Refresh progress data to pick up new sessionStatus
      if (sessionId) {
        progressService.getSessionProgress(sessionId).then((data) => {
          setProgressData(data);
          const completed = data.steps.filter(s => s.status === 'completed').length;
          const allDone = completed === data.steps.length;
          if (allDone) {
            showNotification('This session has ended. You\'re now in review mode.', 'info');
          } else {
            showNotification('Your instructor has ended this session. You can continue working — your desktop is available for 48 hours.', 'info');
          }
        }).catch(() => {});
      }
    },
    onSessionPaused: () => {
      if (sessionId) {
        progressService.getSessionProgress(sessionId).then(setProgressData).catch(() => {});
      }
      showNotification('This session has been paused by your instructor.', 'warning');
    },
    onSessionResumed: () => {
      if (sessionId) {
        progressService.getSessionProgress(sessionId).then(setProgressData).catch(() => {});
      }
      showNotification('Session resumed — you may continue working.', 'success');
    },
  });

  const loadProgress = useCallback(async () => {
    if (!sessionId) return;
    try {
      // Try loading existing progress first — works for both active and completed sessions
      let data: SessionProgress | null = null;
      try {
        data = await progressService.getSessionProgress(sessionId);
      } catch {
        // No progress records yet — need to join (only works for active sessions)
      }

      if (!data || data.steps.length === 0) {
        // Fresh join — creates progress records (requires ACTIVE session)
        if (!joinedRef.current) {
          joinedRef.current = true;
          await progressService.joinSession(sessionId);
        }
        data = await progressService.getSessionProgress(sessionId);
      }

      setProgressData(data);

      const firstIncomplete = data.steps.findIndex((s) => s.status !== 'completed');
      if (firstIncomplete >= 0) {
        setCurrentStepIndex(firstIncomplete);
      } else if (data.steps.length > 0) {
        setCurrentStepIndex(data.steps.length - 1);
        loadCompletionStats();
      }
    } catch {
      joinedRef.current = false;
      showNotification('Failed to load tech eval progress', 'error');
    } finally {
      setLoading(false);
    }
  }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompletionStats = async () => {
    if (!sessionId) return;
    try {
      const stats = await progressService.getCompletionStats(sessionId);
      setCompletionStats(stats);
      setShowCompletion(true);
    } catch {
      // non-critical
    }
  };

  const handlePracticeAgain = () => {
    setPracticeActive(true);
    setShowCompletion(false);
    showNotification('Practice mode — your desktop is interactive. Progress is preserved.', 'success');
  };

  const loadActiveHelpRequest = useCallback(async () => {
    if (!sessionId) return;
    try {
      const requests = await helpRequestService.getBySession(sessionId);
      const active = requests.find(r => r.status === 'open' || r.status === 'in_progress');
      if (active) {
        setActiveHelpRequest(active);
        const msgs = await helpRequestService.getMessages(active.id);
        setHelpMessages(msgs);
      }
    } catch { /* silent */ }
  }, [sessionId]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  useEffect(() => {
    loadActiveHelpRequest();
  }, [loadActiveHelpRequest]);

  // Reconstruct checkedItems from checklistState on step switch
  useEffect(() => {
    if (!progressData) return;
    const currentStep = progressData.steps[currentStepIndex];
    if (currentStep) {
      const items = new Set<string>();
      for (const [itemId, checked] of Object.entries(currentStep.checklistState || {})) {
        if (checked) items.add(itemId);
      }
      setCheckedItems(items);
    }
  }, [currentStepIndex, progressData]);

  // Fetch remote desktop info if any step is remote_desktop type
  const desktopFetchedRef = useRef(false);
  useEffect(() => {
    if (!progressData || !sessionId) return;
    if (desktopFetchedRef.current) return;
    const hasRemoteDesktop = progressData.steps.some(
      (s) => s.stepType === 'remote_desktop',
    );
    if (!hasRemoteDesktop) return;

    desktopFetchedRef.current = true;
    setDesktopLoading(true);
    guacamoleService
      .getDesktop(sessionId)
      .then((info) => setDesktopInfo(info))
      .catch(() => setDesktopError('Could not connect to remote desktop'))
      .finally(() => setDesktopLoading(false));
  }, [sessionId, progressData]);

  // Fetch student's environment credentials
  useEffect(() => {
    if (!sessionId) return;
    sessionService.getMyCredentials(sessionId)
      .then(setStudentCredentials)
      .catch(() => { /* Student may not have credentials — that's fine */ });
  }, [sessionId]);

  // Fetch grace period desktop status for completed sessions
  useEffect(() => {
    if (!sessionId || !progressData) return;
    if (progressData.sessionStatus !== 'completed') {
      setGraceStatus('none');
      return;
    }
    progressService.getMyDesktopStatus(sessionId)
      .then((result) => {
        setGraceStatus(result.status);
        setGraceTtlExpiresAt(result.ttlExpiresAt);
      })
      .catch(() => {
        setGraceStatus('none');
      });
  }, [sessionId, progressData?.sessionStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance for auto_advance criteria
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!progressData) return;
    const currentStep = progressData.steps[currentStepIndex];
    if (!currentStep || currentStep.status !== 'in_progress') return;
    if (currentStep.stepCompletionCriteria !== 'auto_advance') return;

    const totalItems = getChecklistItemCount(currentStep);
    if (totalItems === 0) {
      // No checklists — auto-complete after 3 seconds
      autoAdvanceRef.current = setTimeout(() => {
        handleMarkComplete();
      }, 3000);
    } else if (checkedItems.size >= totalItems) {
      // All checklist items checked — auto-complete
      autoAdvanceRef.current = setTimeout(() => {
        handleMarkComplete();
      }, 500);
    }

    return () => {
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    };
  }, [checkedItems, currentStepIndex, progressData]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist split ratio to localStorage
  useEffect(() => {
    localStorage.setItem('praxis-split-ratio', String(splitRatio));
  }, [splitRatio]);

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem('praxis-instructions-collapsed', String(isInstructionsCollapsed));
  }, [isInstructionsCollapsed]);

  // Keyboard shortcut: Ctrl+B to toggle collapse
  useEffect(() => {
    const handleKeyShortcut = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === 'b' || e.key === '\\')) {
        if (document.activeElement?.tagName === 'IFRAME') return;
        e.preventDefault();
        setIsInstructionsCollapsed(prev => !prev);
      }
    };
    document.addEventListener('keydown', handleKeyShortcut);
    return () => document.removeEventListener('keydown', handleKeyShortcut);
  }, []);

  // Trigger Guacamole resize after panel size changes
  useEffect(() => {
    const timer = setTimeout(() => {
      const iframe = guacIframeRef.current;
      if (iframe?.contentWindow) {
        iframe.contentWindow.dispatchEvent(new Event('resize'));
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [splitRatio, isInstructionsCollapsed]);

  // Resolve Web App URL: instructor sees step's configured URL, student sees their enrollment credentials
  const resolvedWebAppUrl = useMemo(() => {
    const step = progressData?.steps?.[currentStepIndex];
    if (!step || step.stepType !== 'web_app') return null;
    if (user?.role === 'instructor') return step.stepTargetUrl || null;
    if (studentCredentials?.sharingPortalUrl) return studentCredentials.sharingPortalUrl;
    if (studentCredentials?.tenantUrl) return studentCredentials.tenantUrl;
    return null;
  }, [progressData, currentStepIndex, user?.role, studentCredentials]);

  // Resolve RDP URL: use Guacamole embed if assigned, instructor falls back to step URL
  const resolvedRdpUrl = useMemo(() => {
    const step = progressData?.steps?.[currentStepIndex];
    if (!step || step.stepType !== 'remote_desktop') return null;
    if (desktopInfo?.assigned && desktopInfo?.embedUrl) return desktopInfo.embedUrl;
    if (user?.role === 'instructor' && step.stepTargetUrl) return step.stepTargetUrl;
    return null;
  }, [progressData, currentStepIndex, desktopInfo, user?.role]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!progressData || progressData.steps.length === 0) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h6" color="text.secondary">No steps found for this tech eval.</Typography>
        <Button variant="outlined" onClick={() => navigate('/student')}>Back to Dashboard</Button>
      </Box>
    );
  }

  // Session state gating
  if (progressData.sessionStatus === 'scheduled') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Session Hasn't Started Yet</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
          Your instructor will start the session when it's time. Please check back later.
        </Typography>
        <Button variant="outlined" startIcon={<BackIcon />} onClick={() => navigate('/student')}>
          Back to Dashboard
        </Button>
      </Box>
    );
  }

  // Show completion screen only when student just finished all steps during a live session
  if (showCompletion && (progressData.sessionStatus === 'active' || practiceActive)) {
    return (
      <CompletionScreen
        stats={completionStats}
        onLoadStats={loadCompletionStats}
        navigate={navigate}
        onReviewSteps={() => setShowCompletion(false)}
        sessionId={sessionId!}
        showNotification={showNotification}
      />
    );
  }

  const steps = progressData.steps;
  const currentStep = steps[currentStepIndex];
  currentStepTypeRef.current = currentStep?.stepType || 'introduction';
  const completedCount = steps.filter((s) => s.status === 'completed').length;
  const allCompleted = completedCount === steps.length;
  const progressPercent = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;
  const isReadOnly = progressData.sessionStatus === 'completed' && allCompleted && !practiceActive;
  const isPaused = progressData.sessionStatus === 'paused';

  const isStepLocked = (index: number): boolean => {
    if (!progressData.enforceStepOrder) return false;
    if (steps[index].status === 'completed') return false;
    for (let i = 0; i < index; i++) {
      if (steps[i].status !== 'completed') return true;
    }
    return false;
  };

  const handleStepClick = async (index: number) => {
    if (!isReadOnly && isStepLocked(index)) return;
    setCurrentStepIndex(index);
    // Mark as in_progress if not started (skip in read-only mode)
    if (isReadOnly) return;
    const step = steps[index];
    if (step.status === 'not_started') {
      try {
        await progressService.markStepInProgress(step.id);
        const data = await progressService.getSessionProgress(sessionId!);
        setProgressData(data);
      } catch {
        // non-critical — step will show as not_started
      }
    }
  };

  const handleMarkComplete = async () => {
    if (!currentStep || actionLoading) return;
    setActionLoading(true);
    try {
      if (currentStep.status === 'not_started') {
        await progressService.markStepInProgress(currentStep.id);
      }
      await progressService.markStepComplete(currentStep.id);

      const data = await progressService.getSessionProgress(sessionId!);
      setProgressData(data);

      const newCompletedCount = data.steps.filter((s) => s.status === 'completed').length;
      if (newCompletedCount === data.steps.length) {
        loadCompletionStats();
        return;
      }

      const nextIncomplete = data.steps.findIndex((s, i) => i > currentStepIndex && s.status !== 'completed');
      if (nextIncomplete >= 0) {
        setCurrentStepIndex(nextIncomplete);
      }
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      showNotification(message || 'Failed to update progress', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckItem = async (itemId: string) => {
    if (!currentStep) return;
    const wasChecked = checkedItems.has(itemId);
    const newChecked = new Set(checkedItems);
    if (wasChecked) {
      newChecked.delete(itemId);
    } else {
      newChecked.add(itemId);
    }
    const prevChecked = checkedItems;
    setCheckedItems(newChecked); // Optimistic update

    try {
      await progressService.updateChecklistItem(currentStep.id, itemId, !wasChecked);
    } catch {
      setCheckedItems(prevChecked); // Revert on failure
      showNotification('Failed to update checklist', 'error');
    }
  };

  const handleSendHelp = async () => {
    if (!sessionId || helpSending) return;
    setHelpSending(true);
    try {
      const newRequest = await helpRequestService.create(sessionId, {
        stepId: currentStep?.stepId,
        message: helpMessage || undefined,
        priority: helpPriority,
      });
      setHelpOpen(false);
      setHelpMessage('');
      setHelpPriority('normal');
      showNotification('Help request sent! Your instructor will be notified.', 'success');

      setActiveHelpRequest(newRequest);
      const msgs = await helpRequestService.getMessages(newRequest.id);
      setHelpMessages(msgs);
      setActiveDrawer('help');
    } catch (err) {
      const message = axios.isAxiosError(err) ? err.response?.data?.message : undefined;
      showNotification(message || 'Failed to send help request', 'error');
    } finally {
      setHelpSending(false);
    }
  };

  const handleSendHelpMessage = async () => {
    if (!activeHelpRequest || !helpNewMessage.trim() || helpSendingMsg) return;
    setHelpSendingMsg(true);
    try {
      const msg = await helpRequestService.addMessage(activeHelpRequest.id, helpNewMessage.trim());
      setHelpMessages(prev => [...prev, msg]);
      setHelpNewMessage('');
    } catch {
      showNotification('Failed to send message', 'error');
    } finally {
      setHelpSendingMsg(false);
    }
  };

  const handleResolveHelpRequest = async () => {
    if (!activeHelpRequest) return;
    try {
      await helpRequestService.resolve(activeHelpRequest.id);
      setActiveHelpRequest(prev => prev ? { ...prev, status: 'resolved' } : null);
      setResolveConfirmOpen(false);
      showNotification('Help request resolved.', 'success');
    } catch {
      showNotification('Failed to resolve help request', 'error');
    }
  };

  const handleRequestHelpClick = () => {
    if (activeHelpRequest && activeHelpRequest.status !== 'resolved') {
      setActiveDrawer('help');
      setHelpUnreadCount(0);
    } else {
      setHelpOpen(true);
    }
  };

  const handleHelpMessageKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendHelpMessage();
    }
  };

  const formatRelativeTime = (dateStr: string): string => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  const getTimeOnStep = (): string | null => {
    if (!currentStep?.startedAt) return null;
    const mins = Math.round((Date.now() - new Date(currentStep.startedAt).getTime()) / 60000);
    return `${mins} min`;
  };

  // Whether the current step has an environment panel
  const hasEnvironment = currentStep.stepType === 'remote_desktop' || currentStep.stepType === 'web_app';

  // Completion gating helpers
  const stepCompletable = isStepCompletable(currentStep, checkedItems);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* ═══════════════ HEADER BAR (56px) ═══════════════ */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          height: 56,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button size="small" startIcon={<BackIcon />} onClick={() => navigate('/student')} sx={{ color: 'text.secondary' }}>
            Back
          </Button>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
            {progressData.workshopTitle}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>
            Step {currentStepIndex + 1} of {steps.length}
          </Typography>
          <Box sx={{ width: 120, display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={progressPercent}
              sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: 'grey.200' }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {progressPercent}%
            </Typography>
          </Box>
          {!isReadOnly && (
            <Chip
              icon={connectionStatus === 'connected' ? <WifiIcon sx={{ fontSize: 14 }} /> : <WifiOffIcon sx={{ fontSize: 14 }} />}
              label={connectionStatus === 'connected' ? 'Live' : connectionStatus === 'connecting' ? 'Connecting...' : 'Offline'}
              size="small"
              color={connectionStatus === 'connected' ? 'success' : connectionStatus === 'connecting' ? 'warning' : 'default'}
              variant="outlined"
              sx={{ height: 24 }}
            />
          )}
          {!isReadOnly && progressData.allowHelpRequests && (
            <Button
              variant="outlined"
              size="small"
              startIcon={
                activeHelpRequest && activeHelpRequest.status !== 'resolved' ? (
                  <Badge badgeContent={helpUnreadCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 18 }} />
                  </Badge>
                ) : (
                  <HelpIcon />
                )
              }
              onClick={handleRequestHelpClick}
              color={activeHelpRequest && activeHelpRequest.status !== 'resolved' ? 'success' : 'primary'}
            >
              {activeHelpRequest && activeHelpRequest.status !== 'resolved' ? 'Help Active' : 'Request Help'}
            </Button>
          )}
          {!isReadOnly && (
            <Tooltip title="Session Chat">
              <IconButton
                size="small"
                onClick={() => { setActiveDrawer(prev => prev === 'chat' ? null : 'chat'); setChatUnread(0); }}
              >
                <Badge badgeContent={chatUnread} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 16, minWidth: 16 } }}>
                  <ChatIcon sx={{ fontSize: 20 }} />
                </Badge>
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title="WorkBench">
            <IconButton
              size="small"
              onClick={() => setActiveDrawer(prev => prev === 'workbench' ? null : 'workbench')}
              sx={{
                color: activeDrawer === 'workbench' ? '#f59e0b' : undefined,
                background: activeDrawer === 'workbench' ? 'rgba(245, 158, 11, 0.08)' : undefined,
                '&:hover': { background: activeDrawer === 'workbench' ? 'rgba(245, 158, 11, 0.12)' : undefined },
              }}
            >
              <NoteAltOutlinedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ═══════════════ READ-ONLY BANNER ═══════════════ */}
      {isReadOnly && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: '#e3f2fd', borderBottom: '1px solid #90caf9', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: '#1565c0', fontWeight: 500 }}>
            This session has ended. You're reviewing in read-only mode.
            {graceStatus === 'grace' && graceTtlExpiresAt && (
              <> Your desktop is available until {new Date(graceTtlExpiresAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.</>
            )}
          </Typography>
          {graceStatus === 'grace' && (
            <Button
              variant="contained"
              size="small"
              onClick={handlePracticeAgain}
              sx={{ ml: 2, textTransform: 'none', whiteSpace: 'nowrap' }}
            >
              Practice Again
            </Button>
          )}
        </Box>
      )}

      {/* ═══════════════ PAUSED SESSION BANNER ═══════════════ */}
      {isPaused && (
        <Box sx={{ px: 2, py: 1, bgcolor: '#fff3e0', borderBottom: '1px solid #ffcc80', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 500 }}>
            This session has been paused by your instructor. Please wait — the session will resume shortly.
          </Typography>
        </Box>
      )}

      {/* ═══════════════ ALL-COMPLETE REVIEW BANNER ═══════════════ */}
      {!isReadOnly && allCompleted && !showCompletion && (
        <Box sx={{ px: 2, py: 0.75, bgcolor: '#e8f5e9', borderBottom: '1px solid #a5d6a7', flexShrink: 0 }}>
          <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
            You've completed all steps! Feel free to review content or continue practicing.
          </Typography>
        </Box>
      )}

      {/* ═══════════════ MAIN CONTENT ═══════════════ */}
      <Box ref={splitContainerRef} sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Drag overlay — prevents iframe from stealing mouse events */}
        {isDragging && (
          <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, cursor: 'col-resize' }} />
        )}

        {/* ─── Instructions Panel (or Step Rail when collapsed) ─── */}
        {isInstructionsCollapsed && hasEnvironment ? (
          <Box
            sx={{
              width: 48,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: 'grey.50',
              borderRight: '1px solid',
              borderColor: 'divider',
              alignItems: 'center',
              py: 1,
            }}
          >
            <Tooltip title="Expand instructions (Ctrl+B)" placement="right">
              <IconButton
                size="small"
                onClick={() => setIsInstructionsCollapsed(false)}
                sx={{ mb: 1 }}
              >
                <ChevronRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Divider sx={{ width: '80%', mb: 1 }} />
            {steps.map((step, idx) => (
              <Tooltip key={step.id} title={step.stepTitle} placement="right">
                <Box
                  onClick={() => handleStepClick(idx)}
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 0.5,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                    transition: 'all 0.2s',
                    ...(idx === currentStepIndex ? {
                      bgcolor: 'primary.main',
                      color: 'white',
                      boxShadow: '0 0 0 3px rgba(59,130,246,0.3)',
                    } : step.status === 'completed' ? {
                      bgcolor: 'success.light',
                      color: 'success.dark',
                    } : {
                      bgcolor: 'grey.200',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'grey.300' },
                    }),
                  }}
                >
                  {step.status === 'completed' ? '✓' : idx + 1}
                </Box>
              </Tooltip>
            ))}
          </Box>
        ) : (
          <Box
            sx={{
              width: hasEnvironment ? `${splitRatio}%` : '100%',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              transition: hasEnvironment ? 'width 0.3s ease' : undefined,
            }}
          >
            {/* Collapsible Step Navigator */}
            <Box
              onClick={() => setStepListExpanded(!stepListExpanded)}
              sx={{
                px: 2,
                py: 1,
                bgcolor: 'grey.50',
                borderBottom: '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 48,
                flexShrink: 0,
                '&:hover': { bgcolor: 'grey.100' },
              }}
            >
              {currentStep.status === 'completed' ? (
                <CheckIcon sx={{ color: 'success.main', fontSize: 18 }} />
              ) : currentStep.status === 'in_progress' ? (
                <DotIcon sx={{ color: 'primary.main', fontSize: 18 }} />
              ) : (
                <EmptyIcon sx={{ color: 'grey.400', fontSize: 18 }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 600,
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                Step {currentStepIndex + 1} of {steps.length}: {currentStep.stepTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 0.25 }}>
                  {steps.map((s, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: s.status === 'completed' ? 'success.main' : i === currentStepIndex ? 'primary.main' : 'grey.300',
                      }}
                    />
                  ))}
                </Box>
                {stepListExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                {hasEnvironment && (
                  <Tooltip title="Collapse instructions (Ctrl+B)">
                    <IconButton
                      size="small"
                      onClick={(e) => { e.stopPropagation(); setIsInstructionsCollapsed(true); }}
                    >
                      <ChevronLeftIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>

            <Collapse in={stepListExpanded}>
              <Box sx={{ maxHeight: 250, overflowY: 'auto' }}>
                <List dense disablePadding>
                  {steps.map((step, i) => {
                    const locked = isStepLocked(i);
                    return (
                      <Tooltip key={step.id} title={locked ? 'Complete previous steps first' : ''} placement="right">
                        <ListItemButton
                          selected={i === currentStepIndex}
                          onClick={() => { handleStepClick(i); setStepListExpanded(false); }}
                          disabled={locked}
                          sx={{
                            borderLeft: i === currentStepIndex ? '3px solid' : '3px solid transparent',
                            borderColor: i === currentStepIndex ? 'primary.main' : 'transparent',
                            py: 0.75,
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {step.status === 'completed' ? (
                              <CheckIcon sx={{ color: 'success.main', fontSize: 20 }} />
                            ) : locked ? (
                              <LockIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                            ) : i === currentStepIndex ? (
                              <DotIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            ) : (
                              <EmptyIcon sx={{ color: 'grey.400', fontSize: 20 }} />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: i === currentStepIndex ? 600 : 400,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  fontSize: '0.8125rem',
                                }}
                              >
                                {i + 1}. {step.stepTitle}
                              </Typography>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                {stepTypeIcon[step.stepType] || null}
                                {step.stepEstimatedTime && (
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.6875rem' }}>
                                    {step.stepEstimatedTime} min
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </Tooltip>
                    );
                  })}
                </List>
              </Box>
              <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    {completedCount} of {steps.length} completed
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                    {progressPercent}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={progressPercent}
                  sx={{ height: 4, borderRadius: 2, bgcolor: 'grey.200' }}
                />
              </Box>
            </Collapse>

            {/* Scrollable content */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
              {/* Step header */}
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {currentStep.stepTitle}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                <Chip
                  icon={stepTypeIcon[currentStep.stepType] || undefined}
                  label={currentStep.stepType === 'remote_desktop' ? 'Remote Desktop' : currentStep.stepType === 'web_app' ? 'Web App' : 'Introduction'}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24 }}
                />
                {currentStep.stepEstimatedTime && (
                  <Chip
                    icon={<TimerIcon sx={{ fontSize: 14 }} />}
                    label={`${currentStep.stepEstimatedTime} min`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                )}
              </Box>

              {/* Environment credentials panel — only on steps with a remote environment */}
              {studentCredentials && (currentStep.stepType === 'remote_desktop' || currentStep.stepType === 'web_app') && (
                <EnvironmentPanel credentials={studentCredentials} />
              )}

              {/* Block content — use BlockRenderer if blocks exist, fallback to plain text */}
              {currentStep.stepBlocks && currentStep.stepBlocks.length > 0 ? (
                <BlockRenderer
                  blocks={currentStep.stepBlocks}
                  interactive={!isReadOnly}
                  checkedItems={checkedItems}
                  onCheckItem={handleCheckItem}
                  credentials={studentCredentials || undefined}
                />
              ) : (
                <>
                  {currentStep.stepContent && (
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2, lineHeight: 1.7 }}>
                      {currentStep.stepContent.replace(/\\n/g, '\n')}
                    </Typography>
                  )}
                  {currentStep.stepInstructions && (
                    <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>Instructions</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {currentStep.stepInstructions.replace(/\\n/g, '\n')}
                      </Typography>
                    </Paper>
                  )}
                </>
              )}

              {/* Target URL hint for remote desktop steps */}
              {currentStep.stepType === 'remote_desktop' && currentStep.stepTargetUrl && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #90caf9' }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Navigate to this URL in the remote desktop browser:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {currentStep.stepTargetUrl}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Emoji Reaction Bar */}
            {sessionId && !isReadOnly && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'rgba(250,250,250,0.6)' }}>
                <EmojiReactionBar sessionId={sessionId} currentStepIndex={currentStepIndex} />
              </Box>
            )}

            {/* Sticky action area */}
            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              {isReadOnly ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {currentStepIndex > 0 && (
                    <Button variant="outlined" onClick={() => setCurrentStepIndex(currentStepIndex - 1)}>
                      &#8592; Previous
                    </Button>
                  )}
                  {currentStepIndex < steps.length - 1 && (
                    <Button variant="outlined" sx={{ flex: 1 }} onClick={() => setCurrentStepIndex(currentStepIndex + 1)}>
                      Next Step &#8594;
                    </Button>
                  )}
                </Box>
              ) : currentStep.status === 'completed' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip icon={<CheckIcon />} label="Completed" color="success" size="small" />
                    {currentStepIndex < steps.length - 1 && (
                      <Button size="small" variant="outlined" onClick={() => {
                        const next = steps.findIndex((s, i) => i > currentStepIndex && s.status !== 'completed');
                        if (next >= 0) setCurrentStepIndex(next);
                        else setCurrentStepIndex(currentStepIndex + 1);
                      }}>
                        Next Step
                      </Button>
                    )}
                  </Box>
                  {allCompleted && (
                    <Button variant="contained" fullWidth onClick={loadCompletionStats}>
                      Finish Tech Eval
                    </Button>
                  )}
                </Box>
              ) : currentStep.stepCompletionCriteria === 'auto_advance' ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {currentStepIndex > 0 && (
                    <Button variant="outlined" size="small" onClick={() => setCurrentStepIndex(currentStepIndex - 1)}>
                      &#8592; Previous
                    </Button>
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1, textAlign: 'center', fontStyle: 'italic' }}>
                    This step will auto-advance when tasks are complete
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {currentStepIndex > 0 && (
                    <Button variant="outlined" onClick={() => setCurrentStepIndex(currentStepIndex - 1)}>
                      &#8592; Previous
                    </Button>
                  )}
                  {currentStep.stepCompletionCriteria === 'checklist_gated' && !stepCompletable ? (
                    <Tooltip title="Complete all tasks first">
                      <span style={{ flex: 1 }}>
                        <Button variant="contained" fullWidth disabled>
                          Complete All Tasks First
                        </Button>
                      </span>
                    </Tooltip>
                  ) : (
                    <Button
                      variant="contained"
                      sx={{
                        flex: 1,
                        ...(currentStep.stepCompletionCriteria === 'checklist_gated' && stepCompletable ? {
                          animation: 'pulse 2s infinite',
                          '@keyframes pulse': {
                            '0%': { boxShadow: '0 0 0 0 rgba(59,130,246,0.4)' },
                            '70%': { boxShadow: '0 0 0 10px rgba(59,130,246,0)' },
                            '100%': { boxShadow: '0 0 0 0 rgba(59,130,246,0)' },
                          },
                        } : {}),
                      }}
                      onClick={handleMarkComplete}
                      disabled={actionLoading || isPaused}
                    >
                      {actionLoading ? 'Saving...' : isPaused ? 'Session Paused' : '\u2713 Mark Complete & Continue'}
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* ─── Drag Handle ─── */}
        {hasEnvironment && !isInstructionsCollapsed && (
          <Box
            onMouseDown={handleDragStart}
            onDoubleClick={() => setSplitRatio(currentStep.stepSplitRatio || 35)}
            sx={{
              width: 6,
              cursor: 'col-resize',
              bgcolor: isDragging ? 'primary.light' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              '&:hover': { bgcolor: 'action.hover' },
              '&:active': { bgcolor: 'primary.light' },
              transition: 'background-color 0.15s',
            }}
          >
            <Box sx={{ width: 2, height: 32, borderRadius: 1, bgcolor: 'grey.300' }} />
          </Box>
        )}

        {/* ─── Environment Panel (right side of split) ─── */}
        {hasEnvironment && (
          <Box
            sx={{
              width: isInstructionsCollapsed ? 'calc(100% - 48px)' : `${100 - splitRatio}%`,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: isInstructionsCollapsed ? 'none' : '1px solid',
              borderColor: 'divider',
              transition: 'width 0.3s ease',
            }}
          >
              {/* Environment header */}
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: 'grey.50',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexShrink: 0,
                }}
              >
                {currentStep.stepType === 'remote_desktop' ? (
                  <DesktopIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                ) : (
                  <WebAppIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                  {currentStep.stepEnvironmentLabel || (currentStep.stepType === 'remote_desktop' ? 'Remote Desktop' : 'Web Application')}
                </Typography>
                {((currentStep.stepType === 'remote_desktop' && desktopInfo?.assigned) || (currentStep.stepType === 'web_app' && resolvedWebAppUrl)) && (
                  <>
                    <Tooltip title="Copy text from the remote session to your clipboard">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleCopyFromRemote}
                        sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12, textTransform: 'none' }}
                      >
                        Copy
                      </Button>
                    </Tooltip>
                    <Tooltip title="Paste text into the remote session">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handlePasteToRemote}
                        sx={{ minWidth: 'auto', px: 1.5, py: 0.25, fontSize: 12, textTransform: 'none' }}
                      >
                        Paste
                      </Button>
                    </Tooltip>
                    <Dialog
                      open={pasteDialogOpen}
                      onClose={() => setPasteDialogOpen(false)}
                      maxWidth="sm"
                      fullWidth
                    >
                      <DialogTitle sx={{ pb: 1 }}>Paste into Remote Session</DialogTitle>
                      <DialogContent>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                          Paste your text below (Ctrl+V), then click Send.
                        </Typography>
                        <TextField
                          autoFocus
                          multiline
                          minRows={3}
                          maxRows={8}
                          fullWidth
                          variant="outlined"
                          placeholder="Paste your text here..."
                          value={pasteText}
                          onChange={(e) => setPasteText(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              handlePasteDialogSend();
                            }
                          }}
                        />
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setPasteDialogOpen(false)}>Cancel</Button>
                        <Button
                          variant="contained"
                          onClick={handlePasteDialogSend}
                          disabled={!pasteText}
                        >
                          Send to Remote
                        </Button>
                      </DialogActions>
                    </Dialog>
                    <Dialog
                      open={copyDialogOpen}
                      onClose={() => setCopyDialogOpen(false)}
                      maxWidth="sm"
                      fullWidth
                    >
                      <DialogTitle sx={{ pb: 1 }}>Copy from Remote Session</DialogTitle>
                      <DialogContent>
                        {remoteClipboardText ? (
                          <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                              Text copied in the remote desktop:
                            </Typography>
                            <TextField
                              multiline
                              minRows={3}
                              maxRows={8}
                              fullWidth
                              variant="outlined"
                              value={remoteClipboardText}
                              InputProps={{ readOnly: true }}
                            />
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No text copied yet. Select text in the remote desktop and press Ctrl+C first.
                          </Typography>
                        )}
                      </DialogContent>
                      <DialogActions>
                        <Button onClick={() => setCopyDialogOpen(false)}>Close</Button>
                        {remoteClipboardText && (
                          <Button
                            variant="contained"
                            onClick={handleCopyDialogCopy}
                          >
                            Copy to Clipboard
                          </Button>
                        )}
                      </DialogActions>
                    </Dialog>
                  </>
                )}
              </Box>
              {/* Environment content */}
              <Box sx={{ flex: 1, overflow: 'hidden', bgcolor: '#1e1e2e' }}>
                {currentStep.stepType === 'remote_desktop' ? (
                  desktopLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                      <CircularProgress />
                      <Typography sx={{ ml: 2 }}>Connecting to remote desktop...</Typography>
                    </Box>
                  ) : resolvedRdpUrl ? (
                    <iframe
                      ref={guacIframeRef}
                      src={resolvedRdpUrl}
                      title="Remote Desktop"
                      aria-label="Remote desktop session for the current tech eval step"
                      tabIndex={0}
                      style={{ width: '100%', height: '100%', border: 'none' }}
                      allow="clipboard-read; clipboard-write; keyboard-map"
                      onLoad={handleIframeLoad}
                      onClick={focusIframeContent}
                      onMouseEnter={focusIframeContent}
                    />
                  ) : (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'grey.100', flexDirection: 'column', gap: 2 }}>
                      <DesktopIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                      <Typography variant="h6" color="text.secondary">
                        {isReadOnly && (graceStatus === 'expired' || graceStatus === 'destroyed')
                          ? 'Desktop Expired'
                          : 'Remote Desktop Not Available'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {isReadOnly && (graceStatus === 'expired' || graceStatus === 'destroyed')
                          ? 'Your desktop for this session has expired and is no longer available.'
                          : desktopError || 'No desktop assigned. Contact your instructor.'}
                      </Typography>
                    </Box>
                  )
                ) : resolvedWebAppUrl ? (
                  <iframe
                    src={resolvedWebAppUrl}
                    title={currentStep.stepTitle}
                    style={{ width: '100%', height: '100%', border: 'none' }}
                    sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-modals allow-downloads"
                  />
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: 'grey.50', flexDirection: 'column', gap: 2 }}>
                    <WebAppIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                    <Typography variant="h6" color="text.secondary">
                      No Environment URL Assigned
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 400 }}>
                      Your instructor hasn't assigned an environment URL for your account yet.
                      Contact your instructor for assistance.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          )}
      </Box>

      {/* ═══════════════ DIALOGS & OVERLAYS ═══════════════ */}
      <HelpRequestDialog
        open={helpOpen}
        onClose={() => setHelpOpen(false)}
        onSend={handleSendHelp}
        sending={helpSending}
        message={helpMessage}
        onMessageChange={setHelpMessage}
        priority={helpPriority}
        onPriorityChange={setHelpPriority}
        currentStep={currentStep}
        completedCount={completedCount}
        totalSteps={steps.length}
        timeOnStep={getTimeOnStep()}
      />

      {/* Session Chat Drawer */}
      <Drawer
        anchor="right"
        open={activeDrawer === 'chat'}
        onClose={() => setActiveDrawer(null)}
        PaperProps={{ sx: { width: 320, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Session Chat</Typography>
          <IconButton size="small" onClick={() => setActiveDrawer(null)}>
            <CloseOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
        <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5, p: 2 }}>
          {chatMessages.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 4, fontStyle: 'italic' }}>
              No messages yet. Say hello!
            </Typography>
          )}
          {chatMessages.map((msg, i) => (
            <Box key={msg.timestamp + '-' + i} sx={{ display: 'flex', flexDirection: 'column', mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: msg.userRole === 'instructor' ? 'primary.main' : 'text.primary' }}>
                  {msg.userName}
                </Typography>
                {msg.userRole === 'instructor' && (
                  <Chip label="Host" size="small" color="primary" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600 }} />
                )}
              </Box>
              <Typography variant="body2" sx={{ pl: 0.5 }}>{msg.message}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Type a message..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && chatInput.trim()) {
                socketService.sendChatMessage(sessionId!, chatInput.trim());
                setChatInput('');
              }
            }}
            inputProps={{ maxLength: 500 }}
          />
          <Button
            variant="contained"
            size="small"
            disabled={!chatInput.trim()}
            onClick={() => {
              if (chatInput.trim()) {
                socketService.sendChatMessage(sessionId!, chatInput.trim());
                setChatInput('');
              }
            }}
          >
            Send
          </Button>
        </Box>
      </Drawer>

      {/* Conversation Drawer */}
      <Drawer
        anchor="right"
        open={activeDrawer === 'help'}
        onClose={() => setActiveDrawer(null)}
        PaperProps={{ sx: { width: 380, display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Help Request</Typography>
            {activeHelpRequest && (
              <Chip
                label={activeHelpRequest.status === 'open' ? 'Open' : activeHelpRequest.status === 'in_progress' ? 'In Progress' : 'Resolved'}
                size="small"
                color={activeHelpRequest.status === 'resolved' ? 'default' : activeHelpRequest.status === 'in_progress' ? 'warning' : 'info'}
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {activeHelpRequest && activeHelpRequest.status !== 'resolved' && (
              <Button size="small" color="success" onClick={() => setResolveConfirmOpen(true)} sx={{ minWidth: 'auto', fontSize: '0.75rem' }}>
                Resolve
              </Button>
            )}
            <IconButton size="small" onClick={() => setActiveDrawer(null)}>
              <CloseOutlined fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {helpMessages.map((msg) => {
            const isStudent = msg.userId === user?.id;
            const senderName = isStudent
              ? 'You'
              : msg.user
                ? `${msg.user.firstName} ${msg.user.lastName}`.trim()
                : msg.userRole === 'instructor' ? 'Instructor' : 'Unknown';

            return (
              <Box
                key={msg.id}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: isStudent ? 'flex-start' : 'flex-end',
                  maxWidth: '85%',
                  alignSelf: isStudent ? 'flex-start' : 'flex-end',
                  '&:hover .reaction-add-btn': { opacity: 1 },
                }}
              >
                <Box
                  sx={{
                    bgcolor: isStudent ? '#e3f2fd' : '#f5f5f5',
                    borderRadius: isStudent ? '12px 12px 12px 4px' : '12px 12px 4px 12px',
                    px: 1.5,
                    py: 1,
                  }}
                >
                  <Typography variant="body2">{msg.message}</Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, px: 0.5 }}>
                  {senderName} - {formatRelativeTime(msg.createdAt)}
                </Typography>
                {activeHelpRequest && (
                  <MessageReactions
                    helpRequestId={activeHelpRequest.id}
                    messageId={msg.id}
                    reactions={msg.reactions || []}
                    currentUserId={user?.id || ''}
                    onReactionsChange={handleReactionsChange}
                  />
                )}
              </Box>
            );
          })}

          {activeHelpRequest?.status === 'resolved' && (
            <Box sx={{ textAlign: 'center', py: 1 }}>
              <Chip label="Resolved" size="small" color="success" variant="outlined" />
            </Box>
          )}

          <div ref={helpMessagesEndRef} />
        </Box>

        {activeHelpRequest && activeHelpRequest.status !== 'resolved' && (
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <TextField
              size="small"
              multiline
              maxRows={3}
              fullWidth
              placeholder="Type a message..."
              value={helpNewMessage}
              onChange={(e) => setHelpNewMessage(e.target.value)}
              onKeyDown={handleHelpMessageKeyDown}
              disabled={helpSendingMsg}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '20px' } }}
            />
            <IconButton
              color="primary"
              onClick={handleSendHelpMessage}
              disabled={!helpNewMessage.trim() || helpSendingMsg}
              size="small"
            >
              {helpSendingMsg ? <CircularProgress size={20} /> : <SendOutlined />}
            </IconButton>
          </Box>
        )}
      </Drawer>

      {/* Resolve confirmation dialog */}
      <Dialog open={resolveConfirmOpen} onClose={() => setResolveConfirmOpen(false)} maxWidth="xs">
        <DialogTitle>Resolve Help Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Mark as resolved? Your instructor will be notified.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResolveConfirmOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleResolveHelpRequest}>
            Resolve
          </Button>
        </DialogActions>
      </Dialog>

      {/* WorkBench Floating Popup */}
      {activeDrawer === 'workbench' && (
        <Paper
          elevation={0}
          sx={{
            position: 'fixed',
            top: 72,
            right: 16,
            width: 320,
            height: 320,
            maxHeight: 320,
            background: '#fffdf7',
            borderRadius: '14px',
            boxShadow: '0 12px 48px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)',
            display: 'flex',
            flexDirection: 'column',
            zIndex: 1300,
            overflow: 'hidden',
            animation: 'wbPopIn 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
            transformOrigin: 'top right',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #f59e0b, #f97316, #ef4444)',
              borderRadius: '14px 14px 0 0',
            },
            '@keyframes wbPopIn': {
              '0%': { opacity: 0, transform: 'scale(0.92) translateY(-8px)' },
              '100%': { opacity: 1, transform: 'scale(1) translateY(0)' },
            },
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 1.75, pb: 1.25, borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 22,
                height: 22,
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <NoteAltOutlinedIcon sx={{ fontSize: 12, color: 'white' }} />
              </Box>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'grey.800', letterSpacing: '-0.2px' }}>
                WorkBench
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Tooltip title="Copy All">
                <span>
                  <IconButton size="small" onClick={handleCopyAll} disabled={!wbContent} sx={{ color: 'grey.400', '&:hover': { color: 'grey.700', background: 'rgba(0,0,0,0.05)' } }}>
                    <ContentCopyIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Clear">
                <span>
                  <IconButton size="small" onClick={() => setWbClearConfirmOpen(true)} disabled={!wbContent || wbReadOnly} sx={{ color: 'grey.400', '&:hover': { color: 'grey.700', background: 'rgba(0,0,0,0.05)' } }}>
                    <DeleteOutlineIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </span>
              </Tooltip>
              <IconButton size="small" onClick={() => setActiveDrawer(null)} sx={{ color: 'grey.400', '&:hover': { color: 'grey.700', background: 'rgba(0,0,0,0.05)' } }}>
                <CloseOutlined sx={{ fontSize: 14 }} />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ flex: 1, px: 2, py: 1.5, overflow: 'hidden', minHeight: 0 }}>
            {wbLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <CircularProgress size={20} />
              </Box>
            ) : (
              <textarea
                value={wbContent}
                onChange={handleWbChange}
                readOnly={wbReadOnly}
                placeholder={"Jot down commands, URLs, notes...\n\nEverything auto-saves across steps."}
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none',
                  outline: 'none',
                  resize: 'none',
                  background: 'transparent',
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
                  fontSize: '13px',
                  lineHeight: 1.7,
                  color: '#424242',
                  letterSpacing: '-0.1px',
                  overflowX: 'hidden',
                  overflowY: 'auto',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  boxSizing: 'border-box',
                }}
              />
            )}
          </Box>

          {/* Footer */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1, background: 'rgba(0,0,0,0.02)', borderTop: '1px solid rgba(0,0,0,0.04)' }}>
            <Typography sx={{ fontSize: 11, color: wbContent.length >= 10000 ? 'error.main' : 'grey.400', fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>
              {wbContent.length.toLocaleString()} / 10,000
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              {wbSaving ? (
                <Typography sx={{ fontSize: 11, color: 'grey.500', fontWeight: 500 }}>Saving...</Typography>
              ) : wbLastSaved ? (
                <>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', background: '#2e7d32', animation: 'wbStatusPulse 2s ease infinite', '@keyframes wbStatusPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
                  <Typography sx={{ fontSize: 11, color: 'success.main', fontWeight: 500 }}>Saved</Typography>
                </>
              ) : null}
            </Box>
          </Box>
        </Paper>
      )}

      {/* WorkBench Clear Confirmation Dialog */}
      <Dialog open={wbClearConfirmOpen} onClose={() => setWbClearConfirmOpen(false)}>
        <DialogTitle>Clear WorkBench</DialogTitle>
        <DialogContent>
          <Typography>Clear all notes? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWbClearConfirmOpen(false)}>Cancel</Button>
          <Button onClick={() => { setWbContent(''); setWbClearConfirmOpen(false); saveWbContent(''); }} color="error">Clear</Button>
        </DialogActions>
      </Dialog>

      {/* Floating help indicator */}
      {activeHelpRequest && activeDrawer !== 'help' && activeHelpRequest.status !== 'resolved' && (
        <Box
          onClick={() => { setActiveDrawer('help'); setHelpUnreadCount(0); }}
          sx={{
            position: 'fixed', bottom: 24, right: 24, zIndex: 1300,
            bgcolor: 'primary.main', color: 'white',
            borderRadius: '24px', px: 2, py: 1,
            display: 'flex', alignItems: 'center', gap: 1,
            cursor: 'pointer', boxShadow: 4,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <Badge badgeContent={helpUnreadCount} color="error">
            <ChatBubbleOutlineOutlined sx={{ fontSize: 20 }} />
          </Badge>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Help Request Active</Typography>
        </Box>
      )}

      {/* Broadcast snackbar */}
      <Snackbar
        open={!!broadcastMsg}
        autoHideDuration={8000}
        onClose={() => setBroadcastMsg(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="info" variant="filled" onClose={() => setBroadcastMsg(null)}>
          {broadcastMsg}
        </Alert>
      </Snackbar>

      {/* Help resolved toast */}
      <Snackbar
        open={!!helpResolvedMsg}
        autoHideDuration={6000}
        onClose={() => setHelpResolvedMsg(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled" onClose={() => setHelpResolvedMsg(null)}>
          {helpResolvedMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ═══════════════════════════
// Helpers
// ═══════════════════════════

function getChecklistItemCount(step: StepProgress): number {
  let total = 0;
  for (const block of (step.stepBlocks || [])) {
    if (block.type === 'checklist' && block.meta?.items) {
      total += block.meta.items.length;
    }
  }
  return total;
}

function isStepCompletable(step: StepProgress, checkedItems: Set<string>): boolean {
  if (step.stepCompletionCriteria === 'manual') return true;
  if (step.stepCompletionCriteria === 'checklist_gated') {
    const totalItems = getChecklistItemCount(step);
    if (totalItems === 0) return true;
    return checkedItems.size >= totalItems;
  }
  return true;
}

// ═══════════════════════════
// Help Request Dialog
// ═══════════════════════════
interface HelpDialogProps {
  open: boolean;
  onClose: () => void;
  onSend: () => void;
  sending: boolean;
  message: string;
  onMessageChange: (v: string) => void;
  priority: 'normal' | 'high';
  onPriorityChange: (v: 'normal' | 'high') => void;
  currentStep: StepProgress;
  completedCount: number;
  totalSteps: number;
  timeOnStep: string | null;
}

const HelpRequestDialog: React.FC<HelpDialogProps> = ({
  open, onClose, onSend, sending,
  message, onMessageChange,
  priority, onPriorityChange,
  currentStep, completedCount, totalSteps, timeOnStep,
}) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
    <DialogTitle>Request Help from Instructor</DialogTitle>
    <DialogContent>
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.50' }}>
        <Typography variant="caption" color="text.secondary" display="block">
          Current Step: Step {currentStep.stepOrderIndex + 1} &mdash; {currentStep.stepTitle}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          Your Progress: {completedCount} of {totalSteps} steps completed
        </Typography>
        {timeOnStep && (
          <Typography variant="caption" color="text.secondary" display="block">
            Time on this step: {timeOnStep}
          </Typography>
        )}
      </Paper>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>Priority</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        <Chip
          label="Normal"
          variant={priority === 'normal' ? 'filled' : 'outlined'}
          color="default"
          onClick={() => onPriorityChange('normal')}
        />
        <Chip
          label="Urgent"
          variant={priority === 'high' ? 'filled' : 'outlined'}
          color="error"
          onClick={() => onPriorityChange('high')}
        />
      </Box>

      <TextField
        multiline
        rows={4}
        fullWidth
        label="Briefly describe what you need help with (optional)"
        placeholder="e.g., Getting an access denied error on the policy page"
        value={message}
        onChange={(e) => onMessageChange(e.target.value.slice(0, 500))}
        helperText={`${message.length}/500`}
      />
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Cancel</Button>
      <Button variant="contained" onClick={onSend} disabled={sending}>
        {sending ? 'Sending...' : 'Send Help Request'}
      </Button>
    </DialogActions>
  </Dialog>
);

// ═══════════════════════════
// Completion Screen
// ═══════════════════════════
interface CompletionScreenProps {
  stats: CompletionStats | null;
  onLoadStats: () => void;
  navigate: (path: string) => void;
  onReviewSteps?: () => void;
  sessionId: string;
  showNotification: (message: string, severity: 'success' | 'error' | 'info' | 'warning') => void;
}

const CompletionScreen: React.FC<CompletionScreenProps> = ({ stats, onLoadStats, navigate, onReviewSteps, sessionId, showNotification }) => {
  const onLoadStatsRef = useRef(onLoadStats);
  onLoadStatsRef.current = onLoadStats;

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ratings, setRatings] = useState({
    overallRating: 0,
    contentClarityRating: 0,
    handsOnRelevanceRating: 0,
    environmentQualityRating: 0,
  });
  const [difficultyPerception, setDifficultyPerception] = useState<DifficultyPerception | null>(null);
  const [recommendWorkshop, setRecommendWorkshop] = useState<boolean | null>(null);
  const [whatWorkedWell, setWhatWorkedWell] = useState('');
  const [whatCouldImprove, setWhatCouldImprove] = useState('');

  useEffect(() => {
    if (!stats) onLoadStatsRef.current();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    feedbackService.hasSubmitted(sessionId).then(({ submitted }) => {
      setFeedbackSubmitted(submitted);
      setFeedbackLoading(false);
    }).catch(() => setFeedbackLoading(false));
  }, [sessionId]);

  const allRatingsProvided = ratings.overallRating > 0
    && ratings.contentClarityRating > 0
    && ratings.handsOnRelevanceRating > 0
    && ratings.environmentQualityRating > 0;

  const handleSubmitFeedback = async () => {
    setSubmitting(true);
    try {
      await feedbackService.submit(sessionId, {
        ...ratings,
        difficultyPerception: difficultyPerception || undefined,
        recommendWorkshop: recommendWorkshop ?? undefined,
        whatWorkedWell: whatWorkedWell.trim() || undefined,
        whatCouldImprove: whatCouldImprove.trim() || undefined,
      });
      setFeedbackSubmitted(true);
      showNotification('Feedback submitted! Thank you.', 'success');
    } catch (err: any) {
      if (err.response?.status === 409) {
        setFeedbackSubmitted(true);
      } else {
        showNotification('Failed to submit feedback. Please try again.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (start: string | null, end: string | null): string => {
    if (!start || !end) return '--';
    const ms = new Date(end).getTime() - new Date(start).getTime();
    const minutes = Math.round(ms / 60000);
    if (minutes < 60) return `${minutes} min`;
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hrs} hr ${mins} min`;
  };

  const ratingLabels: Array<{ key: keyof typeof ratings; label: string }> = [
    { key: 'overallRating', label: 'Overall Experience' },
    { key: 'contentClarityRating', label: 'Content Clarity' },
    { key: 'handsOnRelevanceRating', label: 'Hands-on Relevance' },
    { key: 'environmentQualityRating', label: 'Environment Quality' },
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 4 }}>
      <Box sx={{ maxWidth: 600, textAlign: 'center' }}>
        <CheckIcon
          sx={{
            fontSize: 80,
            color: 'success.main',
            mb: 2,
            animation: 'scaleIn 0.5s ease-out',
            '@keyframes scaleIn': {
              '0%': { transform: 'scale(0)' },
              '100%': { transform: 'scale(1)' },
            },
          }}
        />
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
          Tech Eval Complete!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Congratulations! You've successfully completed all steps.
        </Typography>

        {stats && (
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <CheckIcon sx={{ color: 'success.main', fontSize: 24, mb: 1 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stats.completedSteps}</Typography>
                <Typography variant="caption" color="text.secondary">Steps Completed</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <TimerIcon sx={{ color: 'primary.main', fontSize: 24, mb: 1 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{formatTime(stats.startTime, stats.endTime)}</Typography>
                <Typography variant="caption" color="text.secondary">Completion Time</Typography>
              </Paper>
            </Grid>
            <Grid size={{ xs: 4 }}>
              <Paper elevation={1} sx={{ p: 3, textAlign: 'center' }}>
                <HelpIcon sx={{ color: 'warning.main', fontSize: 24, mb: 1 }} />
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>{stats.helpRequestCount}</Typography>
                <Typography variant="caption" color="text.secondary">Help Requests</Typography>
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* Feedback Section */}
        {feedbackLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : feedbackSubmitted ? (
          <Paper elevation={0} sx={{ p: 3, mt: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
            <CheckIcon sx={{ color: 'success.main', fontSize: 32, mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Thank you for your feedback!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your ratings help us improve future evals.
            </Typography>
          </Paper>
        ) : (
          <Paper elevation={0} sx={{ p: 3, mt: 3, mb: 3, bgcolor: 'grey.50', borderRadius: 2, textAlign: 'left' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
              Rate Your Experience
            </Typography>

            {/* Star Ratings */}
            {ratingLabels.map(({ key, label }) => (
              <Box key={key} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>{label}</Typography>
                <Rating
                  size="large"
                  value={ratings[key]}
                  onChange={(_, value) => setRatings((prev) => ({ ...prev, [key]: value || 0 }))}
                />
              </Box>
            ))}

            {/* Optional Section Divider */}
            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">Optional</Typography>
            </Divider>

            {/* Difficulty Perception */}
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Difficulty</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={difficultyPerception}
              onChange={(_, val) => setDifficultyPerception(val)}
              sx={{ mb: 2, flexWrap: 'wrap' }}
            >
              <ToggleButton value="too_easy">Too Easy</ToggleButton>
              <ToggleButton value="easy">Easy</ToggleButton>
              <ToggleButton value="just_right">Just Right</ToggleButton>
              <ToggleButton value="hard">Hard</ToggleButton>
              <ToggleButton value="too_hard">Too Hard</ToggleButton>
            </ToggleButtonGroup>

            {/* Recommendation */}
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Would you recommend this workshop?</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant={recommendWorkshop === true ? 'contained' : 'outlined'}
                size="small"
                startIcon={<ThumbUpIcon />}
                onClick={() => setRecommendWorkshop(true)}
                color="success"
              >
                Yes
              </Button>
              <Button
                variant={recommendWorkshop === false ? 'contained' : 'outlined'}
                size="small"
                startIcon={<ThumbDownIcon />}
                onClick={() => setRecommendWorkshop(false)}
                color="error"
              >
                No
              </Button>
            </Box>

            {/* Text Fields */}
            <TextField
              label="What worked well?"
              multiline
              rows={3}
              fullWidth
              value={whatWorkedWell}
              onChange={(e) => setWhatWorkedWell(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              helperText={`${whatWorkedWell.length}/1000`}
              sx={{ mb: 2 }}
            />
            <TextField
              label="What could be improved?"
              multiline
              rows={3}
              fullWidth
              value={whatCouldImprove}
              onChange={(e) => setWhatCouldImprove(e.target.value)}
              inputProps={{ maxLength: 1000 }}
              helperText={`${whatCouldImprove.length}/1000`}
              sx={{ mb: 2 }}
            />

            {/* Submit Button */}
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                disabled={!allRatingsProvided || submitting}
                onClick={handleSubmitFeedback}
                startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                {submitting ? 'Submitting...' : 'Submit Feedback'}
              </Button>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {!feedbackSubmitted && !feedbackLoading ? (
            <Tooltip title="Please submit your feedback first">
              <span>
                <Button variant="contained" size="large" disabled>
                  Back to Dashboard
                </Button>
              </span>
            </Tooltip>
          ) : (
            <Button variant="contained" size="large" onClick={() => navigate('/student')}>
              Back to Dashboard
            </Button>
          )}
          {onReviewSteps && (
            <Button variant="outlined" size="large" onClick={onReviewSteps}>
              Review Steps
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorkshopView;
